import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { DataTable, Button, Icon, LoadingOverlay } from './ui';
import DateFilter from './DateFilter';
import MonitorToolbar from './MonitorToolbar';
import { getGateLogs, setGateLogState } from '../slices';
import { roleLabel } from '../roleLabels';

/*
 * UserLog — the "User Log" view under Event Monitor: session/auth activity
 * (LOGIN/LOGOUT) in rows, laid out like the events feed and with the same filter
 * set (search by user, activity type, date range, customer / site / gate) and the
 * same infinite scroll. Its own "event types" are LOGIN / LOGOUT (not IN/OUT). Data
 * + scoping live in GateLogSlice.
 *
 * sType normalization: the web app writes "LOGIN"/"LOGOUT"; mobile guard logins write
 * the GateType ids "100"/"101" — normalize both to a label.
 */
const normalizeType = (t) => {
  const s = String(t == null ? '' : t).toUpperCase();
  if (s === '100' || s === 'LOGIN') return 'LOGIN';
  if (s === '101' || s === 'LOGOUT') return 'LOGOUT';
  return s || '—';
};

const UserLog = (props) => {
  const {
    logs, logCount, logFilters, logPage, logHasMore,
    logListReload, logListLoading, logListLoadingMore,
    customers, sites, gates, subscribers, accountType, authId,
  } = props;

  const committedSearch = (logFilters.find(f => f.id === 'search')?.value) || '';
  const [searchText, setSearchText] = useState(committedSearch);
  useEffect(() => { setSearchText(committedSearch); }, [committedSearch]);

  const selectValue = (id) => logFilters.find(f => f.id === id)?.value || '';
  const activeType = selectValue('type');
  const dateVal = logFilters.find(f => f.id === 'timestamp')?.value || {};

  // commit a filter change: swap the {id,value} in logFilters and trigger a reload.
  const commit = (nextFilters) => {
    props.setGateLogState({ field: 'logFilters', value: nextFilters });
    props.setGateLogState({ field: 'logListReload', value: logListReload + 1 });
  };
  const setFilter = (id, value) => {
    if (logListLoading) return;
    const others = logFilters.filter(f => f.id !== id);
    commit(value ? [...others, { id, value }] : others);
  };
  const commitSearch = () => { if (!logListLoading) setFilter('search', searchText); };
  const clearSearch = () => { if (logListLoading) return; setSearchText(''); setFilter('search', ''); };
  // committed via the DateFilter modal's Apply (not per keystroke) — see DateFilter
  const applyDates = (range) => {
    if (logListLoading) return;
    const others = logFilters.filter(f => f.id !== 'timestamp');
    commit([...others, { id: 'timestamp', value: range }]);
  };
  const clearDates = () => { if (!logListLoading) commit(logFilters.filter(f => f.id !== 'timestamp')); };

  // request params from the committed filters (timestamp → sDate/eDate) + the page.
  const buildParams = (page) => {
    let params = Object.fromEntries(logFilters.map(f => [f.id, f.value]));
    if (params.timestamp) {
      params.sDate = params.timestamp.sDate;
      params.eDate = params.timestamp.eDate;
      delete params.timestamp;
    }
    params.page = page;
    return params;
  };

  // reload from page 1 on mount + any filter change (no App preload for logs).
  useEffect(() => {
    props.setGateLogState({ field: 'logListLoading', value: true });
    props.getGateLogs({ filters: buildParams(1) });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logListReload]);

  const loadMore = () => {
    if (logListLoading || logListLoadingMore || !logHasMore) return;
    props.setGateLogState({ field: 'logListLoadingMore', value: true });
    props.getGateLogs({ filters: buildParams(logPage + 1), append: true });
  };

  // [86bbwu3px item 4] Manual Refresh — reload page 1 keeping the current filters (bumping
  // logListReload re-fires the load effect). Distinct from Reset, which clears every filter.
  const refresh = () => { if (!logListLoading) props.setGateLogState({ field: 'logListReload', value: logListReload + 1 }); };

  const showReset = logFilters.length > 0;

  const columns = [
    {
      key: 'ts', header: 'Date / Time', width: 100,
      render: (row) => (
        <div className="cellstack">
          <span className="mono">{ moment(row.createdAt).format('hh:mm a') }</span>
          <small>{ moment(row.createdAt).format('MM/DD/YY') }</small>
        </div>
      ),
    },
    {
      key: 'activity', header: 'Activity', width: 98,
      render: (row) => { const t = normalizeType(row.sType); return <span className={`pill pill--${t === 'LOGOUT' ? 'logout' : 'login'}`}>{ t }</span>; },
    },
    { key: 'user', header: 'User', width: 128, render: (row) => <span title={row.GateUser?.sFullName}>{ row.GateUser?.sFullName || '—' }</span> },
    {
      key: 'role', header: 'Role', width: 92,
      render: (row) => row.GateAcl?.id != null
        ? <span className="pill pill--plain">{ roleLabel(row.GateAcl.id) }</span>
        : '—',
    },
    { key: 'site', header: 'Site', width: 120, render: (row) => { const v = row.GateAssignment?.GateSite?.sName; return <span className="sub" title={v} style={{ color: 'var(--text-2)' }}>{ v || '—' }</span>; } },
    { key: 'gate', header: 'Gate', width: 116, render: (row) => { const v = row.GateAssignment?.Gate?.sName; return <span title={v}>{ v || '—' }</span>; } },
    { key: 'customer', header: 'Customer', width: 120, render: (row) => <span className="sub" title={row.GateCustomer?.sName} style={{ color: 'var(--text-2)' }}>{ row.GateCustomer?.sName || '—' }</span> },
    { key: 'device', header: 'Device', width: 200, render: (row) => <span className="sub" title={row.sDevice} style={{ color: 'var(--text-2)' }}>{ row.sDevice || '—' }</span> },
  ];

  return (
    <div className="tablecard userlog">
      {/* 2-row toolbar (shared with the Events feed via MonitorToolbar) — same layout
          so the two views match: row 1 = search + activity chips; row 2 = date +
          customer/site/gate selects (Reset far right). User Log has no Export/guard filter. */}
      <MonitorToolbar
        search={
          /* search by user name — commits on Enter or the Search button; ✕ clears */
          <div className="searchgroup">
            <div className={`search${searchText ? ' search--clearable' : ''}`}>
              <Icon name="search" size={15} />
              <input
                className="input"
                placeholder="Search by user name…"
                value={searchText}
                disabled={logListLoading}
                onChange={e => setSearchText(e.target.value)}
                onKeyUp={e => { if (e.key === 'Enter') commitSearch(); }}
              />
              { searchText &&
                <button type="button" className="search__clear" aria-label="Clear search" title="Clear"
                  onMouseDown={e => e.preventDefault()} onClick={() => (committedSearch ? clearSearch() : setSearchText(''))}>
                  <Icon name="close" size={14} />
                </button>
              }
            </div>
            <Button size="sm" variant="primary" disabled={logListLoading} onClick={commitSearch}>Search</Button>
          </div>
        }
        chips={
          /* activity type chips — this view's "event types" are Login / Logout */
          <div className="chipfilter" role="group" aria-label="Activity">
            <button className={activeType === '' ? 'on' : ''} disabled={logListLoading} onClick={() => setFilter('type', '')}>All</button>
            <button className={activeType === 'LOGIN' ? 'on' : ''} disabled={logListLoading} onClick={() => setFilter('type', 'LOGIN')}>Login</button>
            <button className={activeType === 'LOGOUT' ? 'on' : ''} disabled={logListLoading} onClick={() => setFilter('type', 'LOGOUT')}>Logout</button>
          </div>
        }
        filters={
          /* date ▸ location filters: subscriber ▸ customer ▸ site ▸ gate (site/gate via the guard's
             assignment). Subscriber is Super-only — the backend honors a client subscriberId only for
             unscoped/super callers; every other role is force-scoped to their own subscriber. */
          <>
            <DateFilter value={dateVal} onApply={applyDates} onClear={clearDates} disabled={logListLoading} label="date" />
            { authId === 100 &&
              <select className="select select--sm" value={selectValue('subscriberId')} disabled={logListLoading} onChange={e => setFilter('subscriberId', e.target.value)}>
                <option value="">All subscribers</option>
                { subscribers.map(s => <option key={s.id} value={s.id}>{ s.sName }</option>) }
              </select>
            }
            { accountType !== 'direct' &&
              <select className="select select--sm" value={selectValue('customerId')} disabled={logListLoading} onChange={e => setFilter('customerId', e.target.value)}>
                <option value="">All customers</option>
                { customers.map(c => <option key={c.id} value={c.id}>{ c.sName }</option>) }
              </select>
            }
            <select className="select select--sm" value={selectValue('siteId')} disabled={logListLoading} onChange={e => setFilter('siteId', e.target.value)}>
              <option value="">All sites</option>
              { sites.map(s => <option key={s.id} value={s.id}>{ s.sName }</option>) }
            </select>
            <select className="select select--sm" value={selectValue('gateId')} disabled={logListLoading} onChange={e => setFilter('gateId', e.target.value)}>
              <option value="">All gates</option>
              { gates.map(g => <option key={g.id} value={g.id}>{ g.sName }</option>) }
            </select>
          </>
        }
        actionsBottom={
          <>
            <Button size="sm" variant="ghost" icon={<Icon name="refresh" size={15} />} disabled={logListLoading} onClick={refresh}
              title="Refresh — reload the latest activity (keeps your filters)">Refresh</Button>
            { showReset &&
              <Button size="sm" variant="ghost" icon={<Icon name="close" size={15} />} disabled={logListLoading} onClick={() => commit([])}>Reset</Button>
            }
          </>
        }
      />

      <LoadingOverlay show={logListLoading} label="Loading activity" />

      <DataTable
        className="tbl--events"
        columns={columns}
        rows={logs}
        rowKey={(row) => row.id}
        onScrollEnd={loadMore}
        loadingMore={logListLoadingMore}
        empty="No user activity found"
      />

      <div className="pagination">
        <div className="rowcount">
          Showing <span className="num">{ logs.length.toLocaleString() }</span> of <span className="num">{ (logCount || 0).toLocaleString() }</span> events
          { !logHasMore && logs.length > 0 && !logListLoading && !logListLoadingMore && <span className="sub"> · all loaded</span> }
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  // default the new fields so a persisted store from before this slice's reshape
  // (redux-persist replaces the whole slice) can't crash on undefined.
  logs: state.gatelog.logs || [],
  logCount: state.gatelog.logCount || 0,
  logFilters: state.gatelog.logFilters || [],
  logPage: state.gatelog.logPage || 1,
  logHasMore: state.gatelog.logHasMore || false,
  logListReload: state.gatelog.logListReload || 0,
  logListLoading: state.gatelog.logListLoading || false,
  logListLoadingMore: state.gatelog.logListLoadingMore || false,
  customers: state.customer.customers,
  sites: state.site.sites || [],
  gates: state.gate.gates,
  subscribers: state.subscriber.subscribers || [],
  accountType: state.auth.accountType,
  authId: state.auth.authId,
});
export default connect(mapStateToProps, { getGateLogs, setGateLogState })(UserLog);
